import React, { useState } from 'react';
import { Droplet, Lock, RefreshCw, Plus } from 'lucide-react';
import { recordWaterReading, getTenantLatestReading } from './api';
import { useAppTheme } from '../../contexts/ThemeContext';

// Defined outside component — prevents remount-on-render which steals input focus
const ModalOverlay = ({ children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
    {children}
  </div>
);

/**
 * UpdateWaterReading
 * Props:
 *   tenant             — tenant object (.id, .name)
 *   latestWaterReading — last reading object from parent state (or null)
 *   onSaved(result)    — called after a successful save with the API response
 */
const UpdateWaterReading = ({ tenant, latestWaterReading, onSaved }) => {
  const { palette, Colors } = useAppTheme();
  const bg     = palette.background;
  const txt    = palette.text;
  const border = palette.border;

  const inputClass = `w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2`;
  const inputStyle = { backgroundColor: bg.tertiary, border: `1px solid ${border}`, color: txt.primary };

  // ── dialog state ───────────────────────────────────────────────────────────
  const [open, setOpen]                       = useState(false);
  const [prevReading, setPrevReading]         = useState(0);
  const [currReading, setCurrReading]         = useState('');
  const [waterRate, setWaterRate]             = useState(100);
  const [hasExistingReadings, setHasExisting] = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState(null);

  // result screen
  const [result, setResult]                   = useState(null);

  // override sub-dialog
  const [overrideOpen, setOverrideOpen]       = useState(false);
  const [overrideValue, setOverrideValue]     = useState('');

  // ── open: fetch latest reading ─────────────────────────────────────────────
  const handleOpen = async () => {
    setError(null); setSaving(false); setResult(null); setCurrReading('');
    try {
      const data = await getTenantLatestReading(tenant.id);
      const has  = !!data.hasReadings;
      setHasExisting(has);
      const prev = has && data.latestReading > 0 ? data.latestReading : 0;
      setPrevReading(prev);
      setWaterRate(data.latestRate != null ? data.latestRate : (latestWaterReading?.rate ?? 100));
    } catch {
      const has  = latestWaterReading != null;
      setHasExisting(has);
      setPrevReading(latestWaterReading?.currentReading ?? 0);
      setWaterRate(latestWaterReading?.rate ?? 100);
    }
    setOpen(true);
  };

  // ── save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const curr = parseFloat(currReading);
    if (isNaN(curr) || curr === '')      { setError('Enter a valid current reading'); return; }
    if (curr <= prevReading)             { setError('Current reading must be greater than previous reading'); return; }
    if (waterRate <= 0)                  { setError('Rate must be greater than 0'); return; }
    setSaving(true); setError(null);
    try {
      const readingDate = new Date().toISOString().split('T')[0];
      const res = await recordWaterReading({
        tenantId:       tenant.id,
        currentReading: curr,
        rate:           waterRate,
        previousReading: prevReading,
        readingDate,
      });
      setResult(res);
      if (onSaved) onSaved(res);
    } catch (e) { setError(typeof e === 'string' ? e : 'Failed to save water reading'); }
    finally { setSaving(false); }
  };

  const handleClose = () => { setOpen(false); setResult(null); setError(null); };

  const units  = Math.max(0, parseFloat(currReading || 0) - prevReading);
  const charge = units * waterRate;

  return (
    <>
      {/* Trigger button */}
      <button onClick={handleOpen}
        className="px-3 py-1.5 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors"
        style={{ backgroundColor: Colors.accent.blue }}>
        <Plus className="w-4 h-4" /> Record Reading
      </button>

      {/* ── Main water dialog ──────────────────────────────────────────────── */}
      {open && (
        <ModalOverlay>
          <div className="rounded-xl max-w-md w-full p-6"
            style={{ backgroundColor: bg.secondary, border: `1px solid ${border}` }}>

            {result ? (
              /* ── Result / confirmation screen ───────────────────────────── */
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${Colors.status.success}20` }}>
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      style={{ color: Colors.status.success }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold mb-1" style={{ color: txt.primary }}>Charge Applied</h3>
                  <p className="text-sm" style={{ color: txt.secondary }}>
                    Water charge recorded and applied to {tenant.name}'s account immediately.
                  </p>
                </div>

                <div className="rounded-xl p-4 mb-4 space-y-3"
                  style={{ backgroundColor: bg.tertiary, border: `1px solid ${border}` }}>
                  {[
                    ['Previous Reading', `${result.previousReading} m³`],
                    ['Current Reading',  `${result.currentReading} m³`],
                    ['Units Consumed',   `${result.totalUnits} m³`],
                    ['Rate',             `KES ${result.rate}/m³`],
                  ].map(([label, val]) => (
                    <div key={label} className="flex justify-between text-sm">
                      <span style={{ color: txt.secondary }}>{label}</span>
                      <span className="font-medium" style={{ color: txt.primary }}>{val}</span>
                    </div>
                  ))}
                  <div className="pt-3" style={{ borderTop: `1px solid ${border}` }}>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold" style={{ color: txt.primary }}>Total Charged</span>
                      <span className="text-xl font-bold" style={{ color: Colors.status.error }}>
                        KES {Number(result.totalCharge).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm pt-1">
                    <span style={{ color: txt.secondary }}>New Balance</span>
                    <span className="font-semibold"
                      style={{ color: result.newBalance >= 0 ? Colors.status.success : Colors.status.error }}>
                      KES {Number(result.newBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                <button onClick={handleClose}
                  className="w-full py-2.5 text-white rounded-lg font-medium"
                  style={{ backgroundColor: Colors.accent.blue }}>
                  Done
                </button>
              </>
            ) : (
              /* ── Entry form ─────────────────────────────────────────────── */
              <>
                <h3 className="text-xl font-bold mb-4" style={{ color: txt.primary }}>Record Water Reading</h3>

                {error && (
                  <div className="p-3 rounded-lg text-sm mb-4"
                    style={{ backgroundColor: `${Colors.status.error}15`, border: `1px solid ${Colors.status.error}40`, color: Colors.status.error }}>
                    {error}
                  </div>
                )}

                {/* First-reading banner */}
                {!hasExistingReadings && (
                  <div className="p-3 rounded-lg mb-4 text-sm flex items-start gap-2"
                    style={{ backgroundColor: `${Colors.accent.blue}12`, border: `1px solid ${Colors.accent.blue}40`, color: Colors.accent.blue }}>
                    <Droplet className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>No previous readings found. Enter the current meter value and the starting baseline.</span>
                  </div>
                )}

                {/* Previous reading */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium" style={{ color: txt.secondary }}>
                      Previous Reading
                      {!hasExistingReadings && (
                        <span className="ml-1 text-xs font-normal" style={{ color: txt.muted }}>(starting baseline)</span>
                      )}
                    </label>
                    {hasExistingReadings && (
                      <button
                        onClick={() => { setOverrideValue(String(prevReading)); setOverrideOpen(true); }}
                        className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors"
                        style={{ color: Colors.accent.blue, border: `1px solid ${Colors.accent.blue}50`, backgroundColor: `${Colors.accent.blue}10` }}>
                        <RefreshCw className="w-3 h-3" /> Override
                      </button>
                    )}
                  </div>

                  {hasExistingReadings ? (
                    <>
                      <div className="relative">
                        <input type="number" value={prevReading} readOnly className={inputClass}
                          style={{ ...inputStyle, paddingRight: '2.5rem', opacity: 0.65, cursor: 'not-allowed' }} />
                        <Lock className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2" style={{ color: txt.muted }} />
                      </div>
                      <p className="text-xs mt-1" style={{ color: txt.muted }}>
                        Auto-filled — use Override only if meter was reset or replaced
                      </p>
                    </>
                  ) : (
                    <>
                      <input type="number" min="0"
                        value={prevReading}
                        onChange={e => setPrevReading(e.target.value === '' ? '' : Number(e.target.value))}
                        disabled={saving}
                        placeholder="e.g. 0 for a brand-new meter"
                        className={inputClass} style={inputStyle} />
                      <p className="text-xs mt-1" style={{ color: txt.muted }}>
                        Set to 0 if this is a new meter, or the reading just before this tenant moved in
                      </p>
                    </>
                  )}
                </div>

                {/* Current reading */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>Current Reading</label>
                  <input
                    type="number"
                    value={currReading}
                    onChange={e => setCurrReading(e.target.value)}
                    disabled={saving}
                    placeholder="Enter meter reading now"
                    className={inputClass}
                    style={inputStyle}
                  />
                  {currReading !== '' && parseFloat(currReading) <= prevReading && parseFloat(currReading) > 0 && (
                    <p className="text-xs mt-1" style={{ color: Colors.status.error }}>Must be greater than previous reading</p>
                  )}
                </div>

                {/* Rate */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>
                    Rate (KES per unit)
                    {latestWaterReading && (
                      <span className="ml-1 text-xs font-normal" style={{ color: txt.muted }}>— auto-filled, edit if changed</span>
                    )}
                  </label>
                  <input
                    type="number"
                    value={waterRate}
                    onChange={e => setWaterRate(e.target.value === '' ? '' : Number(e.target.value))}
                    disabled={saving}
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                {/* Calculation summary */}
                <div className="p-4 rounded-lg mb-6"
                  style={{ backgroundColor: bg.tertiary, border: `1px solid ${border}` }}>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm" style={{ color: txt.secondary }}>Units consumed</span>
                    <span className="font-bold text-sm" style={{ color: txt.primary }}>{units.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm" style={{ color: txt.secondary }}>Total charge</span>
                    <span className="font-bold text-sm" style={{ color: Colors.status.error }}>
                      KES {charge.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button onClick={handleClose} disabled={saving}
                    className="px-4 py-2 rounded-lg font-medium"
                    style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${border}` }}>
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={currReading === '' || parseFloat(currReading) <= prevReading || saving}
                    className="px-4 py-2 text-white rounded-lg font-medium"
                    style={{
                      backgroundColor: Colors.accent.blue,
                      opacity: (currReading === '' || parseFloat(currReading) <= prevReading || saving) ? 0.6 : 1
                    }}>
                    {saving ? 'Saving…' : 'Save Reading'}
                  </button>
                </div>
              </>
            )}
          </div>
        </ModalOverlay>
      )}

      {/* ── Override sub-dialog ────────────────────────────────────────────── */}
      {overrideOpen && (
        <ModalOverlay>
          <div className="rounded-xl max-w-sm w-full p-6"
            style={{ backgroundColor: bg.secondary, border: `1px solid ${border}` }}>
            <h3 className="text-lg font-bold mb-3" style={{ color: txt.primary }}>Override Previous Reading</h3>

            <div className="p-3 rounded-lg mb-4 text-sm"
              style={{ backgroundColor: '#FEF3C715', border: '1px solid #F59E0B40', color: '#92400E' }}>
              ⚠️ Only use this if the meter was physically reset or replaced.
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg mb-4"
              style={{ backgroundColor: bg.tertiary, border: `1px solid ${border}` }}>
              <span className="text-sm" style={{ color: txt.secondary }}>Current baseline</span>
              <span className="text-sm font-bold" style={{ color: txt.primary }}>{prevReading}</span>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>New baseline value</label>
              <input
                type="number" min="0"
                value={overrideValue}
                onChange={e => setOverrideValue(e.target.value)}
                className={inputClass} style={inputStyle} autoFocus
              />
              <p className="text-xs mt-1" style={{ color: txt.muted }}>Set to 0 for a full reset, or any starting value</p>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setOverrideOpen(false)}
                className="px-4 py-2 rounded-lg font-medium"
                style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${border}` }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  const v = parseFloat(overrideValue);
                  if (!isNaN(v) && v >= 0) {
                    setPrevReading(v);
                    setCurrReading('');
                    setOverrideOpen(false);
                  }
                }}
                disabled={overrideValue === '' || isNaN(parseFloat(overrideValue)) || parseFloat(overrideValue) < 0}
                className="px-4 py-2 text-white rounded-lg font-medium"
                style={{ backgroundColor: Colors.accent.blue }}>
                Apply
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
};

export default UpdateWaterReading;